/**
 * Contra qué proveedor de identidad loguea ESTA sesión (el "flag de sesión" de la migración
 * a Cognito). `?idp=cognito` lo prende y queda guardado; `?idp=auth0` lo apaga. Si el
 * deploy no tiene la config de Cognito, el flag se ignora: nunca una pantalla rota.
 */
export type IdentityProviderName = "auth0" | "cognito";

export const IDP_STORAGE_KEY = "flashbox.idp";

export interface CognitoFrontConfig {
  /** Emisor OIDC: https://cognito-idp.<region>.amazonaws.com/<poolId> */
  authority: string;
  clientId: string;
  /** Dominio del login hosteado, sin barra final */
  domain: string;
}

export function cognitoConfigFromEnv(
  env: Record<string, string | undefined>
): CognitoFrontConfig | null {
  const authority = env.VITE_COGNITO_AUTHORITY?.trim();
  const clientId = env.VITE_COGNITO_CLIENT_ID?.trim();
  const domain = env.VITE_COGNITO_DOMAIN?.trim().replace(/\/+$/, "");
  if (!authority || !clientId || !domain) return null;
  return { authority, clientId, domain };
}

type FlagStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/**
 * El proveedor por defecto del deploy (VITE_DEFAULT_IDP). Es el interruptor del cutover:
 * pasar a "cognito" y redeployar; volver atrás, lo mismo. Cualquier otro valor es Auth0.
 */
export function defaultIdpFromEnv(env: Record<string, string | undefined>): IdentityProviderName {
  return env.VITE_DEFAULT_IDP?.trim() === "cognito" ? "cognito" : "auth0";
}

/**
 * El proveedor de esta sesión: lo que pidió con ?idp= (y quedó guardado), o el default del
 * deploy. ?idp=auth0 se guarda explícito: con Cognito por defecto es el rollback por persona.
 */
export function resolveSessionIdp(
  search: string,
  storage: FlagStorage,
  cognitoAvailable: boolean,
  defaultIdp: IdentityProviderName = "auth0"
): IdentityProviderName {
  const requested = new URLSearchParams(search).get("idp");
  if (requested === "cognito" || requested === "auth0") storage.setItem(IDP_STORAGE_KEY, requested);
  if (!cognitoAvailable) return "auth0";
  const stored = storage.getItem(IDP_STORAGE_KEY);
  if (stored === "cognito" || stored === "auth0") return stored;
  return defaultIdp;
}

type ClearableStorage = Pick<Storage, "getItem" | "setItem" | "clear">;

/**
 * Vacía el storage (la app lo hace al cerrar sesión o ante un error de auth) sin perder el
 * flag de la sesión: si se perdiera, el próximo login de alguien en Cognito volvería a Auth0.
 */
export function clearLocalStorageKeepingIdp(storage: ClearableStorage = window.localStorage): void {
  const idp = storage.getItem(IDP_STORAGE_KEY);
  storage.clear();
  if (idp !== null) storage.setItem(IDP_STORAGE_KEY, idp);
}
